import {IReg} from 'lib/commons/registry';
import {DOM, JSX} from "lib/commons/xml/dom";
import {IWspUiEnv, JWspUriChangeMsg} from "lib/wsp/wsp";
import {IAction} from "lib/commons/actions";
import {IEndPoint} from "lib/commons/io/io";
import {BoxEnumEntry, IBoxEnumEltWedlet, IBoxEnumProvider, WedEnumProvider} from "back/edit/wed/wedlets/box/boxTags";
import {IItemUiEnv} from "back/wsp/views/itemMain";
import {EWspChangesEvts, IWspXmlHouse, WspsLivePlace} from "lib/wsp/wspsLive";
import {EventMgr} from "lib/commons/events";
import {WedMgr} from "back/edit/wed/wedEditor";
import {JSrcFields, SRC} from "lib/wsp/src";
import {XA} from "lib/commons/xml/xAddr";

/**
 * Appelé au chargement dans le WED :
 * <jslib src=":back:wsp/widgets/wed/enums/enumPrvFromCurrentItem.js"/>
 * Déclare un svc 'wsp-wed-enumprv-currentItem'
 * qui doit être déclaré dans le widget Wed. Exemple :
 * <box-input-enum
 * 		enumSvc="wsp-wed-enumprv-currentItem" transform?=""
 * 		xslUrl?="" msgItemUndef="" contextualXpathBlackListExpVisible?="./title" contextualXpathBlackListExpEnabled?="./title"/>
 */
export async function jslibAsyncInit(jsEndPoint: IEndPoint, elt: Element, reg: IReg<any>) {
	reg.registerSvc('wsp-wed-enumprv-currentItem', 1, (tpl: Element, widget: IBoxEnumEltWedlet) => {
		const wedMgr = widget.wedlet.wedMgr;
		const cache = wedMgr.getDatasForModel(tpl);
		let enumPrv: EnumPrvFromCurrentItem = cache['wsp-wed-enumprv-currentItem'];
		if (!enumPrv) {
			enumPrv = new EnumPrvFromCurrentItem(tpl, wedMgr);
			cache['wsp-wed-enumprv-currentItem'] = enumPrv;
		}
		return enumPrv;
	});
}

/**
 * Fournisseur d'enum issu d'un item
 * <xxx transform?="" xslUrl?=""  msgItemUndef="" contextualXpathBlackListExpVisible?="./title" contextualXpathBlackListExpEnabled?="./title"/>
 */
export class EnumPrvFromCurrentItem extends WedEnumProvider implements IBoxEnumProvider {
	onEnumChange = new EventMgr<() => void>();
	protected initing: Promise<void>;
	protected reg: IReg<IWspUiEnv & IItemUiEnv>;
	protected msgItemUndef: string;
	protected transform?: string;
	protected xslUrl?: IEndPoint;
	/** Si null chargement de la xsl en erreur*/
	protected xslt?: XSLTProcessor | null;
	protected contextualXpathBlackListExpVisible?: XPathExpression;
	protected contextualXpathBlackListExpEnabled?: XPathExpression;
	protected currentBlackListValuesVisible?: Set<String>;
	protected currentBlackListValuesEnabled?: Set<String>;

	get itemSrcUri(): string {
		return this.wedMgr?.reg.env.longDesc.srcUri
	}

	get itemLongDesc(): JSrcFields {
		return this.wedMgr?.reg.env.longDesc;
	}

	constructor(tpl: Element, protected wedMgr: WedMgr) {
		super(null);
		this.reg = wedMgr.reg;
		this.initing = this.init(tpl);
	}

	async init(tpl: Element) {
		try {

			//XPath
			const doc = this.wedMgr.docHolder.getDocument();
			this.msgItemUndef = tpl.getAttribute("msgItemUndef") || "L'item spécifiant les valeurs n'est pas défini";
			if (tpl.hasAttribute("contextualXpathBlackListExpEnabled")) this.contextualXpathBlackListExpEnabled = doc.createExpression(tpl.getAttribute("contextualXpathBlackListExpEnabled"), (prefix) => DOM.lookupNamespaceURI(doc, prefix));
			if (tpl.hasAttribute("contextualXpathBlackListExpVisible")) this.contextualXpathBlackListExpVisible = doc.createExpression(tpl.getAttribute("contextualXpathBlackListExpVisible"), (prefix) => DOM.lookupNamespaceURI(doc, prefix));

			// Xsl
			if (tpl.hasAttribute("xslUrl")) {
				this.xslUrl = this.reg.env.itemType.datas.wspResUrl.resolve(tpl.getAttribute("xslUrl"));
				try {
					const xsl = await this.xslUrl.fetchDom();
					this.xslt = new XSLTProcessor();
					this.xslt.importStylesheet(xsl);
				} catch (e) {
					console.error(e);
					this.xslt = null;
					return;
				}
			}

			this.transform = tpl.getAttribute('transform');

			// Listener sur les changements de l'item pointé
			const place = this.reg.env.place;
			this._wspUriChange = (msg: JWspUriChangeMsg, from) => {
				if (msg.wspCd !== this.reg.env.wsp.code) return;
				if ((msg.type === EWspChangesEvts.r && SRC.isSubUriOrEqual(msg.srcUri, this.itemSrcUri))
					|| (msg.type === EWspChangesEvts.u && this.itemSrcUri === msg.srcUri)) {
					if (this.resetActions()) this.onEnumChange.emit();
				}
			}
			place.eventsMgr.on("wspUriChange", this._wspUriChange);

			this._needReset = () => {if (this.resetActions()) this.onEnumChange.emit()}
			place.eventsMgr.on("wspLiveStateChange", this._needReset);
			place.eventsMgr.on("onConnectionRenewed", this._needReset);

			//Cleanup sur fermeture éditeur.
			this.wedMgr.listeners.on("killEditor", () => {
				const place = this.reg.env.place;
				place.eventsMgr.removeListener("wspUriChange", this._wspUriChange);
				place.eventsMgr.removeListener("wspLiveStateChange", this._needReset);
				place.eventsMgr.removeListener("onConnectionRenewed", this._needReset);
			});

			if (this._actions) {
				// Chargement prématuré d'attente => refresh
				this.resetActions();
				this.onEnumChange.emit();
			}
		} finally {
			this.initing = null; //fin de l'init
		}
	}

	protected _wspUriChange: (msg: JWspUriChangeMsg, from: WspsLivePlace | IWspXmlHouse | 'local' | 'server') => void;
	protected _needReset: () => void;

	makeEnum() {
		if (this.xslt === null) {
			// xsl configurée mais echec à sa construction.
			this._actions = [new BoxEnumEntry(null).setEnabled(false).setLabel("Chargement du composant en erreur")];
			return;
		} else if (this.xslt === undefined) {
			this._actions = [new BoxEnumEntry(null).setEnabled(false).setLabel("Chargement en cours...")];
			return;
		}
		try {
			let dom = this.wedMgr.docHolder.getDocument();
			if (this.xslt) {
				this.xslt.clearParameters();
				for (const entry in this.itemLongDesc) {
					this.xslt.setParameter("", `item.${entry}`, (this.itemLongDesc as any)[entry]);
				}
				dom = this.xslt.transformToDocument(dom);
			}
			this._actions = dom ? WedEnumProvider.buildActions(dom.documentElement, this) : [];
		} catch (e) {
			this._actions = [new BoxEnumEntry(null).setEnabled(false).setLabel("Construction de l'énumération en erreur")];
			throw e;
		}
	}

	isKeyVisible(key: string | null): boolean {
		if (this.currentBlackListValuesVisible?.has(key)) return false;
		return true;
	}

	isKeyEnabled(key: string | null): boolean {
		if (this.currentBlackListValuesEnabled?.has(key)) return false;
		return true;
	}

	/** Retourne true si un reset a eu lieu */
	protected resetActions(): boolean {
		if (this._actions) {
			this._actions = null;
			return true;
		}
		return false;
	}

	getActions(widget: IBoxEnumEltWedlet, filter?: string): IAction<IBoxEnumEltWedlet>[] | Promise<IAction<IBoxEnumEltWedlet>[]> {
		this.makeEnum();
		this.refreshBlackListValues(widget);
		return super.getActions(widget, filter);
	}

	getAction(key: string, widget: IBoxEnumEltWedlet): IAction<IBoxEnumEltWedlet> | null | Promise<IAction<IBoxEnumEltWedlet> | null> {
		if (!this._actions) this.makeEnum();
		this.refreshBlackListValues(widget);
		const action = super.getAction(key, widget);
		if (action) return action;
		// FIXME : remonter une vraie erreur wed
		if (!action && key) return new BoxEnumEntry(null).setEnabled(false).setLabel((ctx) => `Valeur indéterminée : '${key}'`).override("initButtonNode", (buttonNode: Element, ctx: Element) => {
			buttonNode.classList.add("error");
		});
	}

	protected refreshBlackListValues(widget: IBoxEnumEltWedlet): void {
		if (this.contextualXpathBlackListExpEnabled || this.contextualXpathBlackListExpVisible) {
			const wedMgr = widget.wedlet.wedMgr;
			let noVirtualWedlet = widget.wedlet;
			while (noVirtualWedlet?.isVirtual()) noVirtualWedlet = noVirtualWedlet.wedParent;
			const from = noVirtualWedlet ? XA.findDomContainer(noVirtualWedlet.wedAnchor, wedMgr.docHolder.getDocument()) : wedMgr.docHolder.getDocument();
			if (this.contextualXpathBlackListExpEnabled) {
				const res = this.contextualXpathBlackListExpEnabled.evaluate(from, XPathResult.ORDERED_NODE_ITERATOR_TYPE);
				if (this.currentBlackListValuesEnabled)
					this.currentBlackListValuesEnabled.clear();
				else this.currentBlackListValuesEnabled = new Set<string>();
				for (let n = res.iterateNext(); n; n = res.iterateNext()) this.currentBlackListValuesEnabled.add(n.textContent);
			}
			if (this.contextualXpathBlackListExpVisible) {
				const res = this.contextualXpathBlackListExpVisible.evaluate(from, XPathResult.ORDERED_NODE_ITERATOR_TYPE);
				if (this.currentBlackListValuesVisible)
					this.currentBlackListValuesVisible.clear();
				else this.currentBlackListValuesVisible = new Set<string>();
				for (let n = res.iterateNext(); n; n = res.iterateNext()) this.currentBlackListValuesVisible.add(n.textContent);
			}
		}
	}
}